import { WhatsAppTemplateCatalogItem, WhatsAppTemplateComponentParameters } from '../../models';

export interface WhatsAppTemplateParameterDefinition {
    componentType: string;
    valueCount: number;
    label: string;
    description?: string;
}

const placeholderPattern = /\{\{(\d+)\}\}/g;

const getPlaceholderCount = (text?: string): number => {
    if (!text) return 0;

    const matches = [...text.matchAll(placeholderPattern)]
        .map(match => Number(match[1]))
        .filter(Number.isFinite);

    return matches.length === 0 ? 0 : Math.max(...matches);
};

const getComponentValueCount = (component: WhatsAppTemplateCatalogItem['componentsSnapshot'][number]): number => {
    if (component.example?.headerText?.length) {
        return component.example.headerText.length;
    }

    if (component.example?.bodyText?.length) {
        return component.example.bodyText[0]?.length ?? component.example.bodyText.length;
    }

    if (component.buttons?.length) {
        return component.buttons.length;
    }

    return Math.max(getPlaceholderCount(component.text ?? ''), 1);
};

export const buildWhatsAppTemplateParameterDefinitions = (template?: WhatsAppTemplateCatalogItem): WhatsAppTemplateParameterDefinition[] => {
    if (!template) return [];

    return template.componentsSnapshot
        .filter(component => ['HEADER', 'BODY', 'BUTTON'].includes((component.type ?? '').toUpperCase()))
        .map(component => ({
            componentType: component.type.toUpperCase(),
            valueCount: getComponentValueCount(component),
            label: component.type.toUpperCase(),
            description: component.text ?? undefined,
        }));
};

export const seedWhatsAppTemplateParameters = (
    template?: WhatsAppTemplateCatalogItem,
    current?: WhatsAppTemplateComponentParameters[],
): WhatsAppTemplateComponentParameters[] => {
    const definitions = buildWhatsAppTemplateParameterDefinitions(template);

    if (definitions.length === 0) {
        return (current ?? []).map(parameter => ({
            componentType: parameter.componentType,
            values: [...parameter.values],
        }));
    }

    return definitions.map(definition => {
        const existing = current?.find(parameter => parameter.componentType === definition.componentType);
        const values = existing?.values ?? Array.from({ length: definition.valueCount }, () => '');

        return {
            componentType: definition.componentType,
            values: values.slice(0, definition.valueCount).concat(
                Array.from({ length: Math.max(0, definition.valueCount - values.length) }, () => '')
            ),
        };
    });
};

export const updateWhatsAppTemplateParameterValue = (
    parameters: WhatsAppTemplateComponentParameters[] | undefined,
    componentType: string,
    index: number,
    value: string,
): WhatsAppTemplateComponentParameters[] => {
    const nextParameters = (parameters ?? []).map(parameter => ({
        componentType: parameter.componentType,
        values: [...parameter.values],
    }));
    const current = nextParameters.find(parameter => parameter.componentType === componentType);

    if (!current) {
        const values = Array.from({ length: index + 1 }, () => '');
        values[index] = value;
        return [...nextParameters, { componentType, values }];
    }

    const nextValues = [...current.values];
    nextValues[index] = value;
    current.values = nextValues;

    return nextParameters;
};

export const getSelectedTemplate = (
    templates: WhatsAppTemplateCatalogItem[],
    templateId?: string,
): WhatsAppTemplateCatalogItem | undefined =>
    templates.find(template => template.id === templateId);
